import { DocumentDropZone } from "@/components/frontend/DocumentDropZone";
import {
    ChatFileDropContext,
    type ChatFileDropHandler,
} from "@/pages/frontend/chat/contexts/ChatFileDropContext";
import { useCallback, useMemo, useState, type ReactNode } from "react";

interface ChatFileDropZoneProps {
    className?: string;
    children: ReactNode;
}

/** Page-wide drop zone that hands dropped files to the registered composer. */
export const ChatFileDropZone = ({
    className,
    children,
}: ChatFileDropZoneProps) => {
    const [dropHandler, setDropHandler] = useState<ChatFileDropHandler | null>(
        null
    );

    const registerDropHandler = useCallback((handler: ChatFileDropHandler) => {
        setDropHandler(() => handler);
        return () =>
            setDropHandler((current: ChatFileDropHandler | null) =>
                current === handler ? null : current
            );
    }, []);
    const contextValue = useMemo(
        () => ({ registerDropHandler }),
        [registerDropHandler]
    );
    const handleDropFiles = useCallback(
        (files: File[]) => dropHandler?.(files),
        [dropHandler]
    );

    return (
        <ChatFileDropContext.Provider value={contextValue}>
            <DocumentDropZone
                title="Upload to Polly"
                subtitle="Drop PDF, XLSX or CSV files here"
                invalidTypeDescription="not a PDF, XLSX or CSV"
                onDropFiles={handleDropFiles}
                disabled={dropHandler === null}
                allowSpreadsheets
                // Drags over the upload modal, library picker or input gate
                // belong to those dialogs, not to the page.
                ignorePortaledDrags
                className={className}
            >
                {children}
            </DocumentDropZone>
        </ChatFileDropContext.Provider>
    );
};
